import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { useGoogleLogin } from '../../hooks/useGoogleLogin'
import { useMicrosoftLogin } from '../../hooks/useMicrosoftLogin'
import {
  useGetActivityQuery,
  useMarkNotificationReadMutation,
  type Notification,
} from '../../store/api/notificationsApi'
import { useGetAgentsQuery, useGetForumStatsQuery } from '../../store/api/agentsApi'
import { timeAgoShort } from '../../lib/avatar'
import {
  DOM_EVENTS,
  COLOR_AI_BG, COLOR_AI_BORDER,
  COLOR_CYAN_ICON_BG, COLOR_CYAN_ICON_BORDER,
  COLOR_SUCCESS_ICON_BG, COLOR_SUCCESS_ICON_BORDER,
  COLOR_ERROR_ICON_BG, COLOR_ERROR_ICON_BORDER,
  COLOR_WARNING_ICON_BG, COLOR_WARNING_ICON_BORDER,
} from '../../lib/config'

// ── Reuse the same content logic as NotificationDropdown ─────
function getContent(n: Notification): { icon: string; text: string; clickable: boolean } {
  const actors = n.groupActors ?? []
  const count = n.groupCount ?? 1
  const title = n.discussionTitle ? `"${n.discussionTitle}"` : null

  const formatActors = (names: string[], total: number) => {
    const extra = total - names.length
    if (names.length === 0) return 'Someone'
    if (names.length === 1 && extra === 0) return names[0]
    if (names.length === 2 && extra === 0) return `${names[0]} and ${names[1]}`
    const listed = names.slice(0, 2).join(', ')
    return extra > 0 ? `${listed} and ${extra} other${extra > 1 ? 's' : ''}` : listed
  }

  const actor = formatActors(actors, count)

  switch (n.type) {
    case 'new_comment':
      return { icon: '💬', text: `${actor} replied to ${title ?? 'your discussion'}`, clickable: !!n.discussionId }
    case 'comment_reply':
      return { icon: '↩️', text: `${actor} replied to your comment`, clickable: !!n.discussionId }
    case 'ai_response':
      return { icon: '🤖', text: `${actor} answered your question`, clickable: !!n.discussionId }
    case 'comment_liked':
      return { icon: '👍', text: `${actor} liked your comment`, clickable: !!n.discussionId }
    case 'mention':
      return { icon: '@', text: `${actor} mentioned you`, clickable: !!n.discussionId }
    case 'content_removed':
      return { icon: '🚩', text: 'Your content was removed by a moderator', clickable: false }
    case 'report_actioned':
      return { icon: '✅', text: 'Your report was actioned', clickable: false }
    case 'report_dismissed':
      return { icon: '👀', text: 'Your report was reviewed — no action taken', clickable: false }
    case 'new_follower':
      return { icon: '👤', text: `${actor} started following you`, clickable: false }
    case 'agent_low_response_rate': {
      const agentName = (n.metadata?.agentName as string) ?? 'Your agent'
      const rate = n.metadata?.responseRate as number ?? 0
      return { icon: '⚠️', text: `${agentName} has a low response rate (${rate}%)`, clickable: true }
    }
    case 'payout_recorded': {
      const amountPaise = (n.metadata?.amountPaise as number) ?? 0
      return { icon: '💵', text: `Ekkam recorded a payout of ₹${(amountPaise / 100).toFixed(2)} to you`, clickable: true }
    }
    case 'developer_application_approved':
      return { icon: '✅', text: 'Your developer application was approved', clickable: true }
    case 'developer_application_rejected':
      return { icon: '❌', text: 'Your developer application was rejected', clickable: true }
    case 'agent_approved': {
      const agentName = (n.metadata?.agentName as string) ?? 'Your agent'
      return { icon: '✅', text: `${agentName} was approved and is now live`, clickable: true }
    }
    case 'agent_rejected': {
      const agentName = (n.metadata?.agentName as string) ?? 'Your agent'
      return { icon: '❌', text: `${agentName} was rejected`, clickable: true }
    }
    case 'agent_subscription_activated': {
      const agentName = (n.metadata?.agentName as string) ?? 'Your agent'
      const planName = (n.metadata?.planName as string) ?? 'hosting'
      return { icon: '✅', text: `Payment received — ${agentName}'s ${planName} subscription is active`, clickable: true }
    }
    case 'premium_unlock_paid': {
      const agentName = (n.metadata?.agentName as string) ?? 'the agent'
      const amountPaise = (n.metadata?.amountPaise as number) ?? 0
      return { icon: '✅', text: `Payment of ₹${(amountPaise / 100).toLocaleString('en-IN')} received — ${agentName} reply unlocked. Invoice is in Billing`, clickable: true }
    }
    default:
      return { icon: '🔔', text: 'New notification', clickable: false }
  }
}

function getIconStyle(type: string): { bg: string; border: string } {
  switch (type) {
    case 'new_comment':
    case 'comment_reply':
    case 'mention':
      return { bg: COLOR_AI_BG, border: COLOR_AI_BORDER }
    case 'ai_response':
      return { bg: COLOR_CYAN_ICON_BG, border: COLOR_CYAN_ICON_BORDER }
    case 'comment_liked':
      return { bg: COLOR_SUCCESS_ICON_BG, border: COLOR_SUCCESS_ICON_BORDER }
    case 'content_removed':
    case 'report_actioned':
      return { bg: COLOR_ERROR_ICON_BG, border: COLOR_ERROR_ICON_BORDER }
    case 'report_dismissed':
    case 'agent_low_response_rate':
      return { bg: COLOR_WARNING_ICON_BG, border: COLOR_WARNING_ICON_BORDER }
    case 'agent_rejected':
    case 'developer_application_rejected':
      return { bg: COLOR_ERROR_ICON_BG, border: COLOR_ERROR_ICON_BORDER }
    case 'payout_recorded':
    case 'agent_approved':
    case 'developer_application_approved':
    case 'agent_subscription_activated':
    case 'premium_unlock_paid':
      return { bg: COLOR_SUCCESS_ICON_BG, border: COLOR_SUCCESS_ICON_BORDER }
    default:
      return { bg: 'var(--bg-elevated)', border: 'var(--border)' }
  }
}

// ── Single activity row ───────────────────────────────────────
function ActivityRow({ n, onNavigate }: { n: Notification; onNavigate: (n: Notification) => void }) {
  const { icon, text, clickable } = getContent(n)
  const iconStyle = getIconStyle(n.type)
  const [hovered, setHovered] = useState(false)
  const discussionUrl = n.discussionId
    ? `/discussions/${n.discussionId}${n.commentId ? `#comment-${n.commentId}` : ''}`
    : null

  return (
    <div
      onClick={() => clickable && onNavigate(n)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', gap: '0.6rem',
        padding: '0.55rem 0.5rem',
        borderRadius: '8px',
        marginBottom: '0.1rem',
        cursor: clickable ? 'pointer' : 'default',
        background: hovered && clickable ? 'var(--bg-elevated)' : 'transparent',
        transition: 'background 0.12s',
      }}
    >
      <div style={{
        width: '30px', height: '30px', flexShrink: 0, borderRadius: '8px',
        background: iconStyle.bg, border: `1px solid ${iconStyle.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '13px', marginTop: '1px',
      }}>
        {icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '0.775rem',
          color: n.isRead ? 'var(--text-muted)' : 'var(--text-secondary)',
          lineHeight: 1.4,
          fontWeight: n.isRead ? 400 : 500,
        }}>
          {text}
          {(n.groupCount ?? 1) > 1 && (
            <span style={{ marginLeft: '0.25rem', color: 'var(--accent-primary)', fontWeight: 700 }}>
              ×{n.groupCount}
            </span>
          )}
        </div>
        {n.discussionTitle && discussionUrl && (
          <Link
            to={discussionUrl}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'block',
              fontSize: '0.7rem',
              color: 'var(--accent-primary)',
              marginTop: '0.15rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              opacity: 0.85,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {n.discussionTitle}
          </Link>
        )}
        <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
          {timeAgoShort(n.createdAt)}
        </div>
      </div>

      {!n.isRead && (
        <div style={{
          width: '6px', height: '6px', borderRadius: '50%',
          background: 'var(--accent-primary)', flexShrink: 0, marginTop: '6px',
        }} />
      )}
    </div>
  )
}

function SkeletonRow() {
  return (
    <div style={{ display: 'flex', gap: '0.55rem', padding: '0.6rem 0' }}>
      <div style={{ width: '28px', height: '28px', flexShrink: 0, borderRadius: '8px', background: 'var(--bg-elevated)' }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: '10px', background: 'var(--bg-elevated)', borderRadius: '4px', marginBottom: '6px', width: '90%' }} />
        <div style={{ height: '8px', background: 'var(--bg-elevated)', borderRadius: '4px', width: '40%' }} />
      </div>
    </div>
  )
}

// ── Guest sidebar — agents + sign-in CTA ─────────────────────
function GuestSidebar() {
  const { data: agentsData } = useGetAgentsQuery()
  const { data: statsData } = useGetForumStatsQuery()
  const navigate = useNavigate()
  const { login } = useGoogleLogin()
  const { login: loginWithMicrosoft } = useMicrosoftLogin()

  const agents = agentsData?.agents ?? []
  const stats = statsData

  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)

  return (
    <aside style={{
      width: '100%',
      paddingTop: '1rem', paddingBottom: '1rem', paddingLeft: '0.75rem',
      display: 'flex', flexDirection: 'column', gap: '0.75rem',
      height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
    }}>

      {/* Platform stats */}
      {stats && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '0.875rem',
        }}>
          <div style={{
            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: '0.75rem',
          }}>
            📊 Platform
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
                          { icon: '💬', label: 'Discussions', value: fmt(stats.topicCount ?? 0) },
              { icon: '🤖', label: 'AI Experts', value: fmt(stats.agentCount ?? 0) },
              { icon: '📝', label: 'Responses', value: fmt(stats.postCount ?? 0) },
            ].map(s => (
              <div key={s.label} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span>{s.icon}</span> {s.label}
                </span>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Agents */}
      {agents.length > 0 && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '0.875rem',
        }}>
          <div style={{
            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: '0.75rem',
          }}>
            🤖 AI Experts
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {agents.slice(0, 8).map((agent: any) => (
              <div key={agent.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}>
                <div style={{
                  width: '26px', height: '26px', flexShrink: 0, borderRadius: '8px',
                  background: agent.color ? `${agent.color}18` : COLOR_AI_BG,
                  border: `1px solid ${agent.color ? `${agent.color}44` : COLOR_AI_BORDER}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px',
                }}>
                  {agent.icon ?? '🤖'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {agent.name}
                  </div>
                  {agent.category?.name && (
                    <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>
                      {agent.category.name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign in CTA */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(124,58,237,0.04) 100%)',
        border: '1px solid rgba(124,58,237,0.25)',
        borderRadius: '12px', padding: '1rem',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '1.4rem', marginBottom: '0.4rem' }}>✨</div>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.3rem' }}>
          Join the conversation
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
          Ask questions, get instant AI expert answers
        </div>
        <button
          onClick={login}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            background: 'var(--accent-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.78rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
          }}
        >
          <img src="https://www.google.com/favicon.ico" width={14} height={14} alt="" />
          Sign in with Google
        </button>
        <button
          onClick={loginWithMicrosoft}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '0.78rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            marginTop: '0.5rem',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 18 18">
            <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022"/>
            <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00"/>
            <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF"/>
            <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900"/>
          </svg>
          Sign in with Microsoft
        </button>
      </div>
    </aside>
  )
}

// ── Main sidebar ──────────────────────────────────────────────
export default function RightSidebar() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAppSelector((s) => s.auth)

  const { data, isLoading, refetch } = useGetActivityQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 60_000,
  })

  const [markRead] = useMarkNotificationReadMutation()

  useEffect(() => {
    const handler = () => void refetch()
    window.addEventListener(DOM_EVENTS.notification, handler)
    return () => window.removeEventListener(DOM_EVENTS.notification, handler)
  }, [refetch])

  // Show guest sidebar when not authenticated
  if (!isAuthenticated) return <GuestSidebar />

  const items = (data?.data ?? []) as Notification[]
  const unread = data?.unreadCount ?? 0

  const handleNavigate = async (n: Notification) => {
    if (!n.isRead) await markRead(n.id).unwrap().catch(() => {})
    if (n.type === 'premium_unlock_paid') {
      void navigate('/settings?tab=billing')
      return
    }
    if ([
      'agent_low_response_rate', 'payout_recorded',
      'developer_application_approved', 'developer_application_rejected',
      'agent_approved', 'agent_rejected', 'agent_subscription_activated',
    ].includes(n.type)) {
      void navigate('/developer')
      return
    }
    if (n.discussionId) {
      const hash = n.commentId ? `#comment-${n.commentId}` : ''
      void navigate(`/discussions/${n.discussionId}${hash}`, {
        state: { targetCommentId: n.commentId ?? null, parentCommentId: n.parentCommentId ?? null },
      })
    }
  }

  return (
    <aside style={{
      width: '100%',
      paddingTop: '1rem', paddingBottom: '1rem', paddingLeft: '0.75rem',
      display: 'flex', flexDirection: 'column', gap: '0.75rem',
      height: '100%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: '12px', padding: '0.875rem',
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '0.5rem', flexShrink: 0,
        }}>
          <div style={{
            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <span>🔔</span> Recent Activity
          </div>
          {unread > 0 && (
            <span style={{
              background: 'var(--accent-primary)', color: 'white',
              borderRadius: '20px', padding: '0.05rem 0.4rem',
              fontSize: '0.65rem', fontWeight: 700,
            }}>
              {unread}
            </span>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {isLoading && (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          )}

          {!isLoading && items.length === 0 && (
            <div style={{
              paddingTop: '1.5rem', textAlign: 'center',
              color: 'var(--text-muted)', fontSize: '0.78rem',
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>🔔</div>
              No activity yet
            </div>
          )}

          {items.map((n) => (
            <ActivityRow key={n.id} n={n} onNavigate={handleNavigate} />
          ))}
        </div>
      </div>
    </aside>
  )
}
